import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Input,
  Checkbox,
  Row,
  Form,
  Typography,
  Spin,
  ConfigProvider,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import ErrorAlert from '../error_alert';
import { useAtom } from 'jotai';
import {
  staffVerificationDataAtom,
  verificationTokenAtom,
  careProviderConfiguration,
} from '../../store';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../header';
import {
  StaffLoginFormType,
  StaffLoginResponseType,
} from '../../models/verification.interface';
import { usePostQuery } from '../../api/hooks/usePostQuery';

const StaffVerification: React.FC<{
  configLoading: boolean;
  configIdling: boolean;
  staffAnonymousModeEnabled?: boolean;
  displayText: {
    openingMessage?: string;
    welcomeMessage?: string;
  };
}> = ({ configLoading, configIdling, staffAnonymousModeEnabled, displayText }) => {
  const { Paragraph, Title } = Typography;
  const [form] = Form.useForm();
  const anonymousChecked = Form.useWatch('anonymous', form);
  const { careProviderSlug = '' } = useParams();
  const navigate = useNavigate();
  const [, setVerificationToken] = useAtom(verificationTokenAtom);
  const [, setStaffData] = useAtom(staffVerificationDataAtom);
  const [careProviderStyles] = useAtom(careProviderConfiguration);

  const [isPristine, setIsPristine] = useState<boolean>(true);

  const { data, exec, isError, errorStatus, isPending, isSuccess } = usePostQuery<
    StaffLoginFormType,
    StaffLoginResponseType
  >('staff_login');

  // A 409 means the provider has anonymous mode on but no anonymous staff member configured,
  // so anonymity cannot be honored. This is distinct from a failed identity check (401).
  const isAnonymousUnavailable = errorStatus === 409;

  const onFinish = async (fieldsValue: StaffLoginFormType) => {
    const values = {
      firstName: fieldsValue.firstName.trim(),
      lastName: fieldsValue.lastName.trim(),
      email: fieldsValue.email.trim(),
      employeeId: fieldsValue.employeeId.trim(),
      careProviderSlug,
      anonymous: staffAnonymousModeEnabled ? Boolean(fieldsValue.anonymous) : false,
    };
    await exec(values);
    setIsPristine(true);
  };

  const onValuesChange = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    isError && setIsPristine(false);
  };

  useEffect(() => {
    if (isSuccess && data?.token && data?.staffMember) {
      setVerificationToken(data.token);
      setStaffData(data.staffMember);
      navigate(`/${careProviderSlug}/landing-page`);
    }
  });

  return (
    <>
      <Header background={careProviderStyles?.colorBgHeader || 'transparent'} />
      <Row style={{ height: '100%', padding: '50px 32px' }}>
        <Col
          xs={{ offset: 0, span: 24 }}
          md={{ offset: 4, span: 16 }}
          lg={{ offset: 6, span: 12 }}
        >
          {configIdling || configLoading ? <Spin /> : null}
          <Row justify="space-between" align="middle">
            <Title level={2} data-cy="opening_message">
              {displayText?.openingMessage || ''}
            </Title>
          </Row>
          <Col>
            <Paragraph style={{ margin: '40px 0 50px ' }}>
              {displayText?.welcomeMessage || ''}
            </Paragraph>
          </Col>
          <Row>
            <Col span={24}>
              <Title level={4}>First, let&apos;s verify your identity</Title>
              <Form
                className="verification-form"
                onValuesChange={onValuesChange}
                layout="vertical"
                form={form}
                onFinish={onFinish}
                name="staffVerificationForm"
                data-cy="staff_verification_form"
              >
                <Form.Item
                  label="First Name"
                  name="firstName"
                  rules={[
                    { required: true, message: 'Please input your first name' },
                  ]}
                >
                  <Input
                    placeholder="First Name"
                    name="firstName"
                    required
                    size="large"
                    data-cy="first_name"
                  />
                </Form.Item>
                <Form.Item
                  label="Last Name"
                  name="lastName"
                  rules={[
                    { required: true, message: 'Please input your last name' },
                  ]}
                >
                  <Input
                    placeholder="Last Name"
                    name="lastName"
                    required
                    size="large"
                    data-cy="last_name"
                  />
                </Form.Item>
                <Form.Item
                  label="Email"
                  name="email"
                  validateTrigger="onBlur"
                  rules={[
                    { required: true, message: 'Please input your email' },
                    { type: 'email', message: 'Please input a valid email address' },
                  ]}
                >
                  <Input
                    placeholder="Email"
                    name="email"
                    required
                    size="large"
                    data-cy="email"
                  />
                </Form.Item>
                <Form.Item
                  label="Employee ID"
                  name="employeeId"
                  rules={[
                    { required: true, message: 'Please input your employee ID' },
                  ]}
                >
                  <Input
                    placeholder="Employee ID"
                    name="employeeId"
                    required
                    size="large"
                    data-cy="employee_id"
                  />
                </Form.Item>
                {isError && isPristine && (
                  <Form.Item>
                    <ErrorAlert
                      message={
                        isAnonymousUnavailable
                          ? 'Anonymous submission isn\'t available for your organization right now. Uncheck \u201CSubmit Anonymously\u201D to continue.'
                          : 'We couldn\'t validate your identity. Confirm the name, email, and employee ID you entered matches what is in your organization\'s HR system.'
                      }
                    />
                  </Form.Item>
                )}
                {staffAnonymousModeEnabled && (
                  <ConfigProvider
                    theme={{
                      components: {
                        Checkbox: { colorText: 'rgba(0, 0, 0, 0.88)' },
                      },
                    }}
                  >
                    <Form.Item
                      name="anonymous"
                      valuePropName="checked"
                      initialValue={false}
                      style={{ marginBottom: anonymousChecked ? 8 : undefined }}
                    >
                      <Checkbox data-cy="anonymous">
                        Submit Anonymously
                      </Checkbox>
                    </Form.Item>
                    {anonymousChecked && (
                      <Paragraph
                        type="secondary"
                        data-cy="anonymous_note"
                        style={{ fontSize: '13px', marginBottom: '10px' }}
                      >
                        <InfoCircleOutlined style={{ marginRight: '6px' }} />
                        We use your staff information to display the correct surveys, but your responses will be kept completely anonymous.
                      </Paragraph>
                    )}
                  </ConfigProvider>
                )}
                <Form.Item shouldUpdate>
                  {() => (
                    <ConfigProvider
                      theme={{
                        token: {
                          colorPrimary: careProviderStyles.colorPrimary,
                        },
                      }}
                    >
                      <Button
                        type="primary"
                        htmlType="submit"
                        shape="round"
                        data-cy="submit"
                        loading={isPending}
                        block
                        style={{ height: '40px', marginTop: '10px' }}
                        disabled={
                          !form.isFieldsTouched(
                            ['firstName', 'lastName', 'email', 'employeeId'],
                            true,
                          )
                          || !!form
                            .getFieldsError()
                            .filter(({ errors }) => errors.length).length
                        }
                      >
                        Submit
                      </Button>
                    </ConfigProvider>
                  )}
                </Form.Item>
              </Form>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default StaffVerification;
